import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuyspreadsheet.top';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
    inLanguage: 'ru',
    isAccessibleForFree: true,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Главная',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Статьи',
        item: `${siteUrl}/article/yoybuy-spreadsheet-polnoe-rukovodstvo`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <JsonLd data={articleJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <main>
        {/* Article Header */}
        <header className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">Главная</Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <Link to="/article/yoybuy-spreadsheet-polnoe-rukovodstvo" className="hover:text-foreground-700 transition-colors cursor-pointer">Статьи</Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
            </nav>

            {/* Category badge */}
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 text-primary-700 rounded-full text-xs font-semibold mb-5">
              <i className="ri-book-open-line"></i>
              {article.category}
            </span>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-extrabold text-foreground-950 leading-[1.1] text-balance mb-6">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                Обновлено: 4 июля 2026
              </span>
            </div>
          </div>
        </header>

        {/* Featured image */}
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 -mt-8 relative z-10">
          <div className="w-full rounded-2xl overflow-hidden h-52 sm:h-72 md:h-96 shadow-sm">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="py-12 md:py-20 px-6 md:px-10 lg:px-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col gap-10 text-foreground-700 leading-relaxed text-sm md:text-base">
              {article.content.map((section, idx) => (
                <section key={idx}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className="mb-4" dangerouslySetInnerHTML={{ __html: para }} />
                  ))}
                </section>
              ))}

              {/* Internal links to other articles */}
              <section className="border-t border-background-200 pt-10">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  Читайте также
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {articles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 4)
                    .map((related) => (
                      <Link
                        key={related.slug}
                        to={`/article/${related.slug}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                        className="flex items-start gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                      >
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="text-xs font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {related.title}
                          </span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">{related.readTime}</span>
                        </div>
                      </Link>
                    ))}
                </div>
              </section>

              {/* Internal links & CTA */}
              <div className="p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200 flex flex-col sm:flex-row items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                    Готовы начать?
                  </h3>
                  <p className="text-sm text-foreground-500 mb-3">
                    Перейдите на основной сайт и получите доступ к полной таблице YOYBUY Spreadsheet.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      YOYBUY Sheet
                    </a>
                    <Link
                      to="/"
                      className="px-5 py-2.5 border border-background-300 text-foreground-700 rounded-full text-sm font-medium hover:border-foreground-400 transition-all whitespace-nowrap cursor-pointer"
                    >
                      ← На главную
                    </Link>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="w-16 h-16 flex items-center justify-center bg-primary-500/10 rounded-2xl">
                    <i className="ri-database-2-line text-primary-500 text-2xl"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </main>
    </>
  );
}